/** */
import cx from 'classnames';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import ContextMenu from 'app/shared/datatable/context-menu/context-menu';
import GmModal from 'app/shared/modal/modal';
import ListCreationForm from 'app/member/modules/mailing-lists/create/create';
import Breadcrumbs from 'app/shared/breadcrumbs/breadcrumbs';

import { CreateButton } from 'app/shared/button/create';
import { Icon } from 'assets/icons';
import styles from './header.module.css';
import { CreateContactModal } from '../contacts/create';
import { OptionsModal } from 'app/shared/option-modal';
import { useTransactionalService } from 'hooks/transactional';
import { useCampaignService } from 'hooks/mailing/campaign';
import ImportContact from '../contacts/import/import';
import useScreenSize from 'hooks/size';
import { PermissionsContext } from 'contexts/permissions';
import { useMailingConfigService } from 'hooks/mailing/configuration';
import { useWalletService } from 'hooks/sales/wallet';
import { useSubscriptionService } from 'hooks/sales/subscription';
import { SubscriptionPurchaseContext } from 'contexts/checkout/subscription';
import { useTenant } from 'contexts/tenant';
import { ReportGenerationModal } from '../reports/generate';

const MemberAreaHeader = ({ onHamburgerClick }) => {
  const { isMobile, isTablet } = useScreenSize();
  const { permissions } = useContext(PermissionsContext);
  const { openModal, setModuleCode } = useContext(SubscriptionPurchaseContext);
  const { is_agency_managed } = useTenant();
  const history = useNavigate();

  const { creationOptions } = useCampaignService();
  const { fetchConfiguration } = useMailingConfigService();
  const { fetchSubscription } = useSubscriptionService();
  const { creationOptions: trasactionalCreationOptions } = useTransactionalService();
  const { fetchWallet } = useWalletService();

  const { page_title } = useSelector((state) => state.header);
  const user_data = useSelector((state) => state.user_data);

  const [has_subscription, setHasSubscription] = useState(false);
  const [is_dkim_set, setIsDKIMSet] = useState(false);
  const [show_create_audience_modal, setShowCreateAudienceModal] = useState(false);
  const [show_creation_modal, setShowCreationModal] = useState(false);
  const [show_tx_creation_options, setShowTxCreationOptions] = useState(false);
  const [show_creation_options, setShowCreationOptions] = useState(false);
  const [show_upload_modal, setShowUploadModal] = useState(false);
  const [show_report_modal, setShowReportModal] = useState(false);

  useEffect(() => {
    fetchConfiguration().then(({ configurations: [config] }) => {
      if (!config) return;

      const { dns } = config;
      setIsDKIMSet(() => dns && !!dns.length);
    });

    fetchSubscription({ query_string: 'plan_code=campaign&status=active' }).then(
      ({ subscriptions: [subscription] }) => {
        setHasSubscription(subscription && subscription.id);
      }
    );
  }, []);

  useMemo(() => {
    fetchWallet();
  }, [user_data.tenant_id]);

  const create_actions = [
    [
      {
        color: 'green',
        label: (
          <span>
            <Icon width={16} height={16} name="payments" />
            Upgrade Plan
          </span>
        ),
        value: 'upgrade_plan',
        hidden: has_subscription || is_agency_managed
      }
    ],
    [
      {
        label: (
          <span>
            <Icon width={16} height={16} name="person_add" />
            Create Contact
          </span>
        ),
        value: 'create_contact',
        hidden: !permissions['contact:create']
      },
      {
        label: (
          <span>
            <Icon width={16} height={16} name="upload" />
            Import Contact
          </span>
        ),
        value: 'import_contact',
        hidden: !permissions['contact:create']
      },
      {
        label: (
          <span>
            <Icon width={16} height={16} name="group_add" />
            Create Audience
          </span>
        ),
        value: 'create_audience',
        hidden: !permissions['audience:create']
      }
    ]
  ];

  if (!isMobile && !isTablet) {
    create_actions.push([
      {
        label: (
          <span>
            <Icon width={16} height={16} name="campaign" />
            Create Campaign
          </span>
        ),
        value: 'create_campaign',
        hidden: !permissions['campaign:create']
      },
      {
        label: (
          <span>
            <Icon width={16} height={16} name="transactional" />
            Create Transactional
          </span>
        ),
        value: 'create_transactional',
        hidden: !permissions['transactional:create']
      },
      {
        label: (
          <span>
            <Icon width={16} height={16} name="automation" />
            Create Journey
          </span>
        ),
        value: 'create_automation',
        hidden: !permissions['automation:create']
      }
    ]);
  }

  create_actions.push([
    {
      label: (
        <span>
          <Icon width={16} height={16} name="note" />
          Generate Report
        </span>
      ),
      value: 'generate_report'
    }
  ]);

  const handleCreateAction = (data) => {
    if (typeof data !== 'string') return;

    switch (data.toLowerCase()) {
      case 'create_contact':
        setShowCreationModal(true);
        break;
      case 'create_automation':
        history('/editor/automations/new');
        break;
      case 'create_transactional':
        setShowTxCreationOptions(true);
        break;
      case 'create_campaign':
        setShowCreationOptions(true);
        break;
      case 'create_audience':
        setShowCreateAudienceModal(true);
        break;
      case 'import_contact':
        setShowUploadModal(true);
        break;
      case 'upgrade_plan':
        setModuleCode('campaign');
        openModal();
        break;
      case 'generate_report':
        setShowReportModal(true);
        break;
      default:
    }
  };

  return (
    <div className={cx(styles.bgColor)}>
      <header className={cx(styles.wrapper, 'shadow-sd')}>
        <div>
          <div className={styles.pageTitle}>
            <span className={styles.menuIcon} onClick={onHamburgerClick}>
              <Icon name="menu" />
            </span>

            {page_title?.length && (
              <span className={styles.title}>
                <Breadcrumbs crumbs={page_title} />
              </span>
            )}
          </div>

          <div className={styles.actions}>
            <span className={styles.tourAnchor} data-tour="quick-actions">
              <ContextMenu
                actions={create_actions.filter((action) => !action.hidden)}
                callback={handleCreateAction}
                text={
                  <div className={styles.greenBadge}>
                    <Icon name="add" />
                    <span className={styles.quickActionText}>Quick Actions</span>
                  </div>
                }
              />
            </span>
            {isMobile && (
              <CreateButton
                actions={create_actions.filter((action) => !action.hidden)}
                handleUserAction={handleCreateAction}
              />
            )}
          </div>
        </div>
      </header>
      <div>
        <GmModal
          title="Create Audience"
          show_title={true}
          show_modal={show_create_audience_modal}
          onClose={() => setShowCreateAudienceModal(false)}
        >
          <ListCreationForm />
        </GmModal>
        <GmModal
          bodyClassName={styles.importsModal}
          title="Create Contact"
          show_title={true}
          show_modal={show_creation_modal}
          onClose={() => setShowCreationModal(false)}
        >
          <CreateContactModal />
        </GmModal>
        <GmModal
          title="Choose creation mode"
          show_modal={show_tx_creation_options}
          onClose={() => setShowTxCreationOptions(false)}
          bodyClassName={styles.optionsModal}
          show_title
        >
          <OptionsModal options={trasactionalCreationOptions} is_dkim_set={is_dkim_set} />
        </GmModal>
        <GmModal
          title="Choose creation mode"
          show_modal={show_creation_options}
          onClose={() => setShowCreationOptions(false)}
          bodyClassName={styles.optionsModal}
          show_title
        >
          <OptionsModal options={creationOptions} is_dkim_set={is_dkim_set} />
        </GmModal>
        <GmModal
          bodyClassName={styles.importsModal}
          title="Import Contacts"
          show_title={true}
          show_modal={show_upload_modal}
          onClose={() => setShowUploadModal(false)}
        >
          <ImportContact />
        </GmModal>
        <GmModal
          title="Generate Report"
          show_title
          show_modal={show_report_modal}
          onClose={() => setShowReportModal(false)}
        >
          <ReportGenerationModal />
        </GmModal>
      </div>
    </div>
  );
};

export default MemberAreaHeader;
